import { useState } from "react";
import { Compass, Gauge, Menu, Settings, Siren } from "lucide-react";

import { ReactComponent as HefringShield } from "assets/hefring-shield.svg";
import { Color } from "utils/theme";
import { useStore } from "hooks/use-store";
import { Screens } from "navigation/routes";
import { Avatar } from "components/ui/avatar";
import { Button } from "components/ui/button";
import { Drawer } from "components/ui/drawer";

interface MenuItemProps {
  onClick: () => void;
  icon?: JSX.Element;
  isActive?: boolean;
  badge?: number | null;
  /** Name this control reports to analytics */
  name: string;
}

const MenuItem = ({
  onClick,
  icon,
  badge = null,
  isActive,
  name,
}: MenuItemProps) => {
  return (
    <Button
      size="icon-lg"
      variant={isActive ? "secondary" : "sidebar"}
      onClick={onClick}
      className="relative"
      data-analytics={`sidebar.${name}`}
    >
      {icon}
      {badge && badge > 0 ? (
        <div
          className="absolute top-1 right-1 w-3 h-3 rounded-full flex items-center justify-center"
          style={{
            background: Color.Rose[600],
            color: Color.White,
          }}
        />
      ) : null}
    </Button>
  );
};

export const Sidebar = () => {
  const [showMenu, setShowMenu] = useState<boolean>(false);

  const alertCounter = useStore((state) => state.alertCounter);
  const screen = useStore((state) => state.screen);
  const tripCaptain = useStore((state) => state.tripCaptain);
  const config = useStore((state) => state.config);
  const setScreen = useStore((state) => state.setScreen);

  return (
    <>
      <div
        className="hidden h-full w-fit sm:flex flex-col items-center justify-between z-50 overflow-hidden overflow-y-auto border-r p-2 lg:p-3"
        style={{
          background: Color.Primary[900],
          borderColor: Color.Primary[700],
        }}
      >
        <div>
          <div className="size-14 p-2 flex items-center justify-center mb-6 ">
            <HefringShield width={"100%"} height={"100%"} />
          </div>

          <div className="flex flex-col justify-around space-y-4">
            <MenuItem
              name="dashboard"
              onClick={() => setScreen(Screens.Dashboard)}
              isActive={screen === Screens.Dashboard}
              icon={<Gauge className="w-8 h-8" />}
            />
            <MenuItem
              name="info"
              onClick={() => setScreen(Screens.Info)}
              isActive={screen === Screens.Info}
              icon={<Compass className="w-8 h-8" />}
            />
            <MenuItem
              name="alerts"
              onClick={() => setScreen(Screens.Alerts)}
              isActive={screen === Screens.Alerts}
              badge={alertCounter}
              icon={<Siren className="w-8 h-8" />}
            />
            <MenuItem
              name="settings"
              onClick={() => setScreen(Screens.Settings)}
              isActive={screen === Screens.Settings}
              icon={<Settings className="w-8 h-8" />}
            />
          </div>
        </div>
        {config?.captains?.length && (
          <Button
            onClick={() => setScreen(Screens.CaptainSelection)}
            variant={tripCaptain ? "primary" : "sidebar"}
            size="icon-lg-rounded"
            data-analytics="sidebar.captain"
          >
            <Avatar
              size="lg"
              name={tripCaptain?.name ?? ""}
              anonymous={!tripCaptain}
              style={{
                backgroundColor: "transparent",
              }}
            />
          </Button>
        )}
      </div>
      <button
        aria-label="sidebar-menu-toggle"
        onClick={() => setShowMenu((prev) => !prev)}
        className="absolute z-50 top-2 right-2 flex sm:hidden items-center justify-center rounded-md shadow-lg p-2"
        style={{
          backgroundColor: Color.Primary[700],
        }}
      >
        <Menu />
      </button>
      <Drawer showMenu={showMenu} setShowMenu={setShowMenu} />
    </>
  );
};
